import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { _ } from "../utils/i18n";
import Button from "./button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownShortWide } from "@fortawesome/free-solid-svg-icons";
import { useRef } from "react";
import { useWindowSize } from "../utils/use-window-size.js";

const OuterWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const SortWrapper = styled.div`
  width: fit-content;
  display: ${({ $isModal }) => ($isModal === true ? "block" : "flex")};
  margin: ${({ $isModal }) =>
    $isModal === true ? "0 0 1rem 0" : "1rem 0 1rem 0"};
  position: relative;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
  }
`;

const ArrowIcon = styled(FontAwesomeIcon)`
  margin-inline: 0.5rem;
`;

const SortButtonWrapperDesktop = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
`;

const SortButtonWrapperMobile = styled.div`
  background-color: var(--color-body);
  margin-inline: 1rem;
  max-width: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SortOptions = styled.div`
  display: flex;
  min-height: ${({ $isOptionsListVisible }) =>
    $isOptionsListVisible === true ? "75px" : "0"};
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin-inline: ${({ $isModal }) => ($isModal === true ? "1rem" : "0")};
  background-color: ${({ $isModal }) =>
    $isModal === true ? "var(--color-body)" : "var(--color-white)"};
  border: ${({ $isOptionsListVisible }) =>
    $isOptionsListVisible === true ? "1px solid var(--color-grey)" : "none"};
  box-shadow: ${({ $isModal }) =>
    $isModal === true ? "none" : "var(--box-shadow)"};
  z-index: 99;
  transition: min-height 0.4s ease-in-out;
  margin-top: ${({ $isModal }) => ($isModal === true ? "0" : "3.7rem")};
  position: ${({ $isModal }) => ($isModal === true ? "relative" : "absolute")};
  border-radius: 5px;
  &:last-child {
    padding-bottom: ${({ $isModal }) => ($isModal === true ? "1rem" : "0")};
  }
  @media (hover: hover) {
    &:hover {
      box-shadow: var(--box-shadow);
      border: 1px solid var(--color-accent2);
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: auto;
    border: none;
  }
`;

const SortLabelOnButton = styled.label`
  font-family: var(--font-headings);
  margin-inline: 0.5rem;
  cursor: pointer;
`;

const Option = styled.div`
  background-color: ${({ $isModal }) =>
    $isModal === true ? "var(--color-body)" : "var(--color-white)"};
  max-width: 100%;
  display: ${({ $isOptionsListVisible }) =>
    $isOptionsListVisible === true ? "flex" : "none"};
  flex-direction: row-reverse;
  justify-content: flex-end;
  border-radius: 5px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: flex;
  }

  span {
    margin-top: 0.1rem;
  }

  input[type="radio"] {
    height: 1.3rem;
    cursor: pointer;
    width: 1.3rem;
    margin-right: 0.5rem;
    appearance: none;
    -webkit-appearance: none;
    border-radius: 50%;
    background: var(--color-white);
    border: 2px solid var(--color-primary);
  }
  input[type="radio"]:checked {
    height: 1.2rem;
    width: 1.2rem;
    margin-right: 0.5rem;
    appearance: none;
    -webkit-appearance: none;
    border-radius: 50%;
    background: var(--color-primary);
    border: 4px solid var(--color-white);
    box-shadow: 0 0 0 2px var(--color-primary);
  }
  label {
    color: var(--color-primary);
    display: flex;
    cursor: pointer;
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: var(--color-body);
    }
  }
`;
const OptionLabel = styled.label`
  width: 100%;
  padding: 0.6rem 0.625em;
`;

/** Using {...props} because we need a callback in productCategory to access order string */
const ProductSorter = ({ ...props }) => {
  const [orderby, setOrderBy] = React.useState("relevans");
  const [isOptionsListVisible, setOptionsListVisible] = React.useState(false);
  const [label, setLabel] = React.useState("Relevans");
  const optionChoice = useRef();
  const windowSize = useWindowSize();

  React.useEffect(() => {
    if (windowSize?.width <= 1024) {
      setOptionsListVisible(true);
    } else {
      setOptionsListVisible(false);
    }
  }, [windowSize]);
  return (
    <OuterWrapper>
      <SortWrapper $isModal={props.isModal}>
        {props.isModal ? (
          <SortButtonWrapperMobile>
            <h2 style={{ fontSize: "clamp(1.2rem,2.5vw,1.5rem)" }}>
              {_("Sortera: ")}
            </h2>
          </SortButtonWrapperMobile>
        ) : (
          <SortButtonWrapperDesktop>
            <Button
              onClick={() => setOptionsListVisible((prev) => !prev)}
              variant="outlined"
              backgroundColor="var(--color-white)"
              textColor="var(--color-black)"
            >
              <span style={{ paddingLeft: "1rem" }}>{_("Sortere: ")}</span>
              <SortLabelOnButton>{label ?? label}</SortLabelOnButton>
              <ArrowIcon icon={faArrowDownShortWide} />
            </Button>
          </SortButtonWrapperDesktop>
        )}
        <SortOptions
          $isOptionsListVisible={isOptionsListVisible}
          $isModal={props.isModal}
        >
          <Option
            $isModal={props.isModal}
            $isOptionsListVisible={isOptionsListVisible}
          >
            <OptionLabel
              htmlFor="title"
              onClick={() => {
                setOrderBy("title");
                setLabel("Tittel");
                setOptionsListVisible(false);
              }}
            >
              <input
                ref={optionChoice}
                id="title"
                type="radio"
                value="title"
                name="Tittel"
                checked={orderby === "title"}
                onChange={(e) => {
                  props.onChange(e);
                }}
              />
              <span>{_(" Tittel")}</span>
            </OptionLabel>
          </Option>
          {!props.isCompany && (
            <>
              <Option
                $isModal={props.isModal}
                $isOptionsListVisible={isOptionsListVisible}
              >
                <OptionLabel
                  htmlFor="price_asc"
                  onClick={() => {
                    setOrderBy("price_asc");
                    setLabel("Pris: Lav till høy");
                    setOptionsListVisible(false);
                  }}
                >
                  <input
                    ref={optionChoice}
                    id="price_asc"
                    type="radio"
                    checked={orderby === "price_asc"}
                    onChange={(e) => {
                      props.onChange(e);
                    }}
                    value="price_asc"
                  />
                  <span>{_(" Pris: Lav till høy")}</span>
                </OptionLabel>
              </Option>
              <Option
                $isModal={props.isModal}
                $isOptionsListVisible={isOptionsListVisible}
              >
                <OptionLabel
                  htmlFor="price_desc"
                  onClick={() => {
                    setOrderBy("price_desc");
                    setLabel("Pris: Høy till lav");
                    setOptionsListVisible(false);
                  }}
                >
                  <input
                    ref={optionChoice}
                    id="price_desc"
                    type="radio"
                    checked={orderby === "price_desc"}
                    onChange={(e) => {
                      props.onChange(e);
                    }}
                    value="price_desc"
                  />
                  <span> {_(" Pris: Høy till lav")}</span>
                </OptionLabel>
              </Option>
            </>
          )}
          <Option
            $isModal={props.isModal}
            $isOptionsListVisible={isOptionsListVisible}
          >
            <OptionLabel
              htmlFor="relevans"
              onClick={() => {
                setOrderBy("relevans");
                setLabel("Relevans");
                setOptionsListVisible(false);
              }}
            >
              <input
                ref={optionChoice}
                id="relevans"
                type="radio"
                checked={orderby === "relevans"}
                onChange={(e) => {
                  props.onChange(e);
                }}
                value="relevans"
              />
              <span>{_(" Relevans")}</span>
            </OptionLabel>
          </Option>
        </SortOptions>
      </SortWrapper>
    </OuterWrapper>
  );
};

ProductSorter.propTypes = {
  onChange: PropTypes.func,
  isCompany: PropTypes.bool,
  isModal: PropTypes.bool,
};

export default ProductSorter;
